import { Slot, Tabs as TabsPrimitive } from "radix-ui";
import type * as React from "react";
import { cn } from "@/lib/utils";

/** Primer's UnderlineNav. The strip overlaps the border below it so the selected underline covers it. */
const underlineList = "-mb-px flex gap-2 overflow-x-auto [scrollbar-width:none]";

/** Selected when it is the current page (router links) or the active tab (Radix Tabs). */
const underlineItem = cn(
  "relative z-0 flex h-12 shrink-0 items-center gap-2 whitespace-nowrap border-0 bg-transparent px-2 text-sm leading-none text-foreground hover:no-underline [&_svg]:text-muted-foreground",
  "before:absolute before:inset-x-0 before:inset-y-2 before:-z-10 before:rounded-md before:transition-colors hover:before:bg-neutral-muted",
  "after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-md",
  "aria-[current=page]:font-semibold aria-[current=page]:after:bg-underline aria-[current=page]:[&_svg]:text-foreground",
  "data-[state=active]:font-semibold data-[state=active]:after:bg-underline data-[state=active]:[&_svg]:text-foreground",
);

function UnderlineNav({ className, ...props }: React.ComponentProps<"nav">) {
  return <nav data-slot="underline-nav" className={cn(underlineList, className)} {...props} />;
}

/** Wraps a router link: `<UnderlineNavItem current><Link …/></UnderlineNavItem>`. */
function UnderlineNavItem({ className, current, ...props }: React.ComponentProps<typeof Slot.Root> & { current?: boolean }) {
  return <Slot.Root aria-current={current ? "page" : undefined} className={cn(underlineItem, className)} {...props} />;
}

const Tabs = TabsPrimitive.Root;
const TabsContent = TabsPrimitive.Content;

function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  return <TabsPrimitive.List data-slot="tabs-list" className={cn(underlineList, className)} {...props} />;
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return <TabsPrimitive.Trigger data-slot="tabs-trigger" className={cn(underlineItem, className)} {...props} />;
}

/** Tab text that reserves its bold width, so selecting a tab never shifts its neighbours. */
function TabLabel({ children }: { children: string }) {
  return (
    <span
      data-text={children}
      className="inline-flex flex-col items-center after:invisible after:h-0 after:select-none after:overflow-hidden after:font-semibold after:content-[attr(data-text)]"
    >
      {children}
    </span>
  );
}

export { TabLabel, Tabs, TabsContent, TabsList, TabsTrigger, UnderlineNav, UnderlineNavItem };
